/* eslint-disable import/no-import-module-exports */
/// <reference types="webpack-env" />
import { AmplifyError, AmplifyErrorCode } from '@aws-amplify/core/internals/utils';
import { composeWithDevToolsDevelopmentOnly } from '@redux-devtools/extension';
import * as Sentry from '@sentry/react';
import { AuthError } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { createStore, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { thunk } from 'redux-thunk';

import configureAwsServices, { Auth } from 'config/amplify/configureAwsServices';
import { restClient } from 'config/store/restClient';
import { IS_PRODUCTION } from 'constants/Global';
import { signOutEmergency } from 'modules/auth/actions';
import magicLinkSaga from 'modules/auth/sagas/magicLink';
import routerReducer from 'modules/router/reducer';
import socketSagas from 'modules/sockets/socket.middleware';
import fleetbackReducers from 'reducers';
import { getDefaultHeaders, sliceResponseFormatter } from 'utils/RequestUtils';
import { actionTransformer, stateTransformer } from 'utils/sentry.utils';

import rootSaga from '../../sagas';

// AWS
configureAwsServices();

// Redux
const sagaMiddleware = createSagaMiddleware();
const websocketMiddleware = createSagaMiddleware();
const magicLinkMiddleware = createSagaMiddleware();
const sentryReduxEnhancer = Sentry.createReduxEnhancer({ actionTransformer, stateTransformer });

// TODO: Migrate to Redux Toolkit, see https://redux.js.org/usage/migrations/migrating-rtk-2
const store = createStore(
  fleetbackReducers(routerReducer),
  composeWithDevToolsDevelopmentOnly(applyMiddleware(thunk, sagaMiddleware, websocketMiddleware, magicLinkMiddleware), sentryReduxEnhancer)
);

// Axios Interceptors: They need the store to be defined
// Refresh token
restClient.interceptors.request.use(async config => {
  let Authorization = config.headers?.Authorization;

  try {
    // Check user session
    const { tokens } = await Auth.fetchAuthSession();

    if (tokens) {
      // User is logged in. Set auth header on all requests
      Authorization = `Bearer ${tokens.idToken?.toString()}`;
    }
  } catch (error) {
    /* eslint-disable no-console */
    // By default, any error when getting/refreshing the token disconnects the user, but...
    let forceDisconnect = true;

    // this will break eventually upon manually upgrading Amplify - it will explicitly break at npm run start - seems like the safest option
    const isAmplifyNetworkError = error instanceof AmplifyError && error?.name === AmplifyErrorCode.NetworkError;
    // ...if it is a network error, we DON'T force disconnection.
    if (error instanceof AuthError || isAmplifyNetworkError) {
      const networkErrorRegex = /network error/i;
      const isNetworkError = isAmplifyNetworkError || networkErrorRegex.test((error.underlyingError as Error)?.message);
      forceDisconnect = !isNetworkError;
    }

    if (forceDisconnect) {
      Authorization = null;

      // Amplify couldn't get user session: clean login data from store, and display login form
      console.warn('Invalid session:', error);

      try {
        store.dispatch(signOutEmergency());
      } catch (signoutError) {
        console.error('Error during user logout:', signoutError);

        // Panic 😱
        localStorage.clear();
        window.location.reload();

        return config;
      }
    } else {
      console.warn('Network error when trying to refresh token:', error);
      // We try to use the previous token as it is not supposed to be expired
      const { idToken } = await cognitoUserPoolsTokenProvider.tokenOrchestrator.tokenStore?.loadTokens() ?? {};
      Authorization = `Bearer ${idToken?.toString()}`;
    }
    /* eslint-enable no-console */
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization,
      ...getDefaultHeaders()
    } as any // TODO: Fix type
  };
});

// Add a response interceptor to avoid content in response when there is no data returned.
restClient.interceptors.response.use(
  response => {
    // Do something with response data
    if (response?.data?.page?.totalElements === 0 || response?.data?.content?.[0]?.collectionValue) {
      response.data.content = [];
    }
    return sliceResponseFormatter(response);
  },
  error => {
    // Do something with response error
    // eslint-disable-next-line no-console
    console.error(error);
    return Promise.reject(error);
  }
);

sagaMiddleware
  .run(rootSaga)
  .toPromise()
  .catch(error => {
    if (IS_PRODUCTION) {
      Sentry.captureException(error);
    }
    if (window.location.pathname !== '/error') {
      window.location.href = '/error';
    }
  });

websocketMiddleware.run(socketSagas);
magicLinkMiddleware.run(magicLinkSaga);

// Webpack
if (!IS_PRODUCTION && module.hot) {
  // Enable Webpack hot module replacement for reducers
  module.hot.accept('../../reducers', () => {
    store.replaceReducer(fleetbackReducers(routerReducer));
  });
}

// Cypress: if it's executed from Cypress, we attach the redux store to the window
if ('Cypress' in window && window.Cypress) {
  // eslint-disable-next-line no-underscore-dangle
  (window as any).__store__ = store;
}

export { store, restClient };
